import React, { useState, useRef, useEffect } from 'react';
import { SendHorizontal, Mic, Image, MessageSquare, Check, Brain } from 'lucide-react';
import { Message, ChatStyle } from '../types';

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  isTyping: boolean;
  chatStyle: ChatStyle;
  onChangeChatStyle: (style: ChatStyle) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  messages, 
  onSendMessage, 
  isTyping,
  chatStyle,
  onChangeChatStyle
}) => {
  const [input, setInput] = useState('');
  const [showStyleConfirm, setShowStyleConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      onSendMessage(input);
      setInput('');
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStyleChange = (style: ChatStyle) => {
    onChangeChatStyle(style);
    setShowStyleConfirm(true);
    setTimeout(() => setShowStyleConfirm(false), 2000);
  };

  const getMessageStyle = (message: Message) => {
    if (message.sender === 'user') {
      return 'bg-blue-500 text-white rounded-tr-none';
    }
    
    if (chatStyle === 'logical') {
      return 'bg-slate-800 text-white rounded-tl-none prose prose-invert max-w-none';
    }
    
    return 'bg-white text-gray-800 shadow-md rounded-tl-none';
  };

  return (
    <div className="flex flex-col w-full h-full bg-gray-50 rounded-xl shadow-inner">
      <div className="p-3 bg-white border-b border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={() => handleStyleChange('cbt')}
            className={`
              relative flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300
              ${chatStyle === 'cbt'
                ? 'bg-blue-500 text-white shadow-lg scale-[1.02]'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span>CBT Style</span>
              {chatStyle === 'cbt' && showStyleConfirm && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1">
                  <Check className="w-4 h-4 text-blue-500" />
                </div>
              )}
            </div>
          </button>
          <button
            onClick={() => handleStyleChange('casual')}
            className={`
              relative flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300
              ${chatStyle === 'casual'
                ? 'bg-blue-500 text-white shadow-lg scale-[1.02]'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span>Casual</span>
              {chatStyle === 'casual' && showStyleConfirm && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1">
                  <Check className="w-4 h-4 text-blue-500" />
                </div>
              )}
            </div>
          </button>
          <button
            onClick={() => handleStyleChange('logical')}
            className={`
              relative flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300
              ${chatStyle === 'logical'
                ? 'bg-slate-800 text-white shadow-lg scale-[1.02]'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
            `}
          >
            <div className="flex items-center justify-center gap-2">
              <Brain className="w-4 h-4" />
              <span>Logical</span>
              {chatStyle === 'logical' && showStyleConfirm && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1">
                  <Check className="w-4 h-4 text-slate-800" />
                </div>
              )}
            </div>
          </button>
        </div>
        {showStyleConfirm && (
          <div className="mt-2 text-center text-sm text-blue-600 animate-fade-in">
            {chatStyle === 'cbt' ? 'Switched to therapeutic style' : 
             chatStyle === 'casual' ? 'Switched to friendly chat style' :
             'Switched to logical analysis style'}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] p-3 rounded-2xl ${getMessageStyle(message)}`}
              >
                {message.content}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className={`p-3 rounded-2xl ${
                chatStyle === 'logical' ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'
              } shadow-md rounded-tl-none`}>
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-gray-200">
        <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Message your AI companion..."
            className="flex-1 bg-transparent outline-none"
          />
          <button 
            type="button" 
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <Mic size={20} />
          </button>
          <button 
            type="button" 
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <Image size={20} />
          </button>
          <button 
            type="submit" 
            className="p-2 text-blue-500 hover:text-blue-700"
          >
            <SendHorizontal size={20} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;